import { useCallback } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { useFtpStore } from '../../store/ftpStore';
import { INTERNAL_DND_MIME } from '../../utils/ftpRowDrop';
import { buildArchiveCommand, ARCHIVE_PROBE, parseArchiveProbe } from '../../utils/archiveCommand';
import { errText, joinRemote, parentRemote } from './ftpViewHelpers';

/** FtpView's server-side actions and drops: archive on the server, moves, and the pane drop handlers. */
export function useFtpRemoteActions({
  t, remotePath, activeConnId, remoteNamesAt, setNameModal, setZipHelp, showNotice, startPathUpload,
  startDownload, uploadBrowserFile, transferBatch, downloadFolder, uploadFolder,
  uploadDroppedFolder,
}) {
  /**
   * Archive the selection **on the server**, over the SFTP connection's own
   * host, via `ssh_term_exec`. Nothing is downloaded, nothing is re-uploaded,
   * and no new Rust was needed — which is exactly why this shape was chosen
   * over zipping locally.
   *
   * **Every filename is single-quoted and `./`-prefixed** (see
   * utils/archiveCommand.js). A file called `; rm -rf ~ #` is a legal name on a
   * server and would otherwise run; one called `-t` would otherwise be read as
   * an option. This is the one place in the FTP view where remote data reaches
   * a shell.
   *
   * The tool is whatever `openCompress` found on the server — `zip`, else
   * `python3`'s zipfile, which writes a real `.zip` with nothing to install. A
   * name ending `.tar.gz` uses `tar` regardless, since that is what was asked.
   */
  const doArchive = async (name, targets, tool) => {
    if (name.includes('/') || name.includes('\\')) {
      throw new Error(t('ftp.archiveBadName'));
    }
    if ((await remoteNamesAt(remotePath)).has(name)) {
      throw new Error(t('ftp.nameExists', { name }));
    }
    // The command — tool choice, `./` prefixes, quoting — is built in
    // utils/archiveCommand.js, which is executed for real in tests against
    // hostile filenames. Nothing about it is assembled here.
    const command = buildArchiveCommand({
      dir: remotePath,
      name,
      files: targets.map((x) => x.name),
      tool,
    });
    const out = await invoke('ssh_term_exec', {
      connId: activeConnId,
      command,
      // Archiving a large tree is not a 30-second job, and the far side caps
      // this at 300 anyway.
      timeoutSecs: 300,
    });
    if (out?.exitCode !== 0) {
      // The server's own message: it says far more than "it failed".
      throw new Error((out?.stderr || '').trim() || `exit ${out?.exitCode}`);
    }
    await useFtpStore.getState().listRemote(remotePath);
    showNotice(t('ftp.archiveCreated', { name }));
  };

  /**
   * Ask the server what it can archive with, BEFORE asking the user for a name.
   * One quick round trip; the answer decides whether they see the name dialog
   * or the "install zip" dialog, so they never type a name for an action that
   * was always going to fail.
   */
  const openCompress = async (targets) => {
    let tool = 'none';
    try {
      const out = await invoke('ssh_term_exec', {
        connId: activeConnId,
        command: ARCHIVE_PROBE,
        timeoutSecs: 15,
      });
      tool = parseArchiveProbe(out?.stdout);
    } catch (err) {
      // Could not even run the probe (the shell is unreachable). That is a
      // connection problem, not a missing tool, and saying "install zip" here
      // would send the user to fix the wrong thing.
      showNotice(errText(err));
      return;
    }
    if (tool === 'none') {
      setZipHelp(true);
      return;
    }
    setNameModal({
      mode: 'archive',
      targets,
      tool,
      defaultName: targets.length === 1 ? `${targets[0].name}.zip` : 'archive.zip',
    });
  };

  /**
   * Move remote entries into `destPath`. **The one implementation of a move**,
   * shared by drag-onto-a-folder, drag-onto-`..`, and the "Move to…" action, so
   * the guards below cannot exist on one path and be missing from another.
   *
   * `ftp_rename` is exactly the primitive: on both SFTP and FTP, renaming to a
   * path in another directory IS the move, so this needs no new Rust.
   *
   * Four guards, and each of them is a way to destroy data or hang:
   *  - a folder cannot be moved into ITSELF;
   *  - nor into its own DESCENDANT, which on a server that allows it detaches
   *    the subtree and on one that does not fails halfway through a batch;
   *  - an entry already IN the destination is skipped, not renamed onto itself;
   *  - and a name that already exists at the destination is **refused**, because
   *    `ftp_rename` on a live server may happily replace it.
   */
  const moveEntriesTo = useCallback(
    async (destPath, entries, destLabel) => {
      const dest = destPath;
      const movable = [];
      for (const ent of entries || []) {
        if (ent.path === dest) continue;
        if (parentRemote(ent.path) === dest) continue;
        // `dest` sitting under `ent.path` means moving a folder into itself.
        if (ent.isDir && (dest === ent.path || dest.startsWith(`${ent.path}/`))) continue;
        movable.push(ent);
      }
      if (movable.length === 0) return;

      // ONE listing for the whole batch, so the overwrite check costs a single
      // round trip rather than one per file. It doubles as the check that the
      // destination exists and is a readable directory at all.
      let taken = new Set();
      try {
        const existing = await invoke('ftp_list_dir', {
          connId: activeConnId,
          path: dest,
          showHidden: true,
        });
        taken = new Set((existing || []).map((x) => x.name));
      } catch {
        // Unreadable or not a directory. Refusing the whole move is the safe
        // answer: without the listing there is no way to know what a rename
        // would replace.
        showNotice(t('ftp.moveFailed', { name: destLabel || dest }));
        return;
      }

      const failed = [];
      for (const ent of movable) {
        if (taken.has(ent.name)) {
          failed.push(t('ftp.nameExists', { name: ent.name }));
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await useFtpStore.getState().renameRemote(ent.path, joinRemote(dest, ent.name));
        } catch (err) {
          failed.push(`${ent.name}: ${errText(err)}`);
        }
      }
      if (failed.length) showNotice(failed.join('; '));
    },
    // `showNotice` is a `[]`-dep callback; listed because exhaustive-deps is an
    // error in this build, not because this should be rebuilt more often.
    [activeConnId, t, showNotice]
  );

  /**
   * Drop a dragged selection onto a folder ROW (or the `..` row) = MOVE.
   *
   * **Remote only.** The local pane is the user's own disk and has no move
   * command behind it; lighting up a local folder and then doing nothing would
   * be worse than not lighting up.
   */
  const makeRowDropHandler = useCallback(
    (side) => async (folder, e) => {
      if (side !== 'remote') return;
      const raw = e.dataTransfer.getData(INTERNAL_DND_MIME);
      let payload = null;
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = null;
      }
      if (!payload || payload.source !== 'remote') return;
      await moveEntriesTo(folder.path, payload.entries, folder.name);
    },
    [moveEntriesTo]
  );

  // `destDir`: a remote FOLDER ROW the drop landed on (OS files, or a drag from
  // the local pane) — the files go INTO it. Omitted, the pane's directory.
  const makeDropHandler = useCallback(
    (side) => (e, destDir) => {
      const raw = e.dataTransfer.getData(INTERNAL_DND_MIME);
      if (raw) {
        let payload = null;
        try {
          payload = JSON.parse(raw);
        } catch {
          payload = null;
        }
        if (!payload || payload.source === side) return;
        const files = [];
        for (const ent of payload.entries || []) {
          if (ent.isDir) {
            // A folder is a tree, not a transfer: plan it, create it, then queue
            // its files. Was "folders are not supported" and silently skipped.
            if (side === 'remote') uploadFolder(ent.path, ent.name, destDir);
            else downloadFolder(ent);
            continue;
          }
          files.push(ent);
        }
        // Files go as ONE batch, so a clash is asked about once and "... all"
        // covers the rest of this drop.
        if (side === 'remote') {
          transferBatch(
            'upload',
            files.map((ent) => ({ name: ent.name, start: (meta) => startPathUpload(ent.path, ent.name, destDir, meta) })),
            destDir
          );
        } else {
          transferBatch(
            'download',
            files.map((ent) => ({ name: ent.name, start: (meta) => startDownload(ent, undefined, meta) }))
          );
        }
        return;
      }
      // OS drop. LOCAL pane: files are already local — ignore.
      if (side !== 'remote') return;
      // DataTransfer.items and .files are NOT guaranteed index-aligned, so
      // zipping files[i] ↔ items[i] can mis-flag a file as a directory (and
      // vice-versa). Derive each entry from the DataTransferItemList via
      // webkitGetAsEntry PER ITEM — synchronously, since the list is emptied
      // once this handler returns — then read each entry's own File.
      const items = Array.from(e.dataTransfer.items || []);
      const entries = items
        .map((it) => (it && typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
        .filter(Boolean);
      if (entries.length === 0) {
        // No entry API (or plain file list) — upload the raw files as-is.
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) return;
        transferBatch(
          'upload',
          files.map((f) => ({ name: f.name, start: (meta) => uploadBrowserFile(f, destDir, meta) })),
          destDir
        );
        return;
      }
      const fileEntries = [];
      for (const entry of entries) {
        if (entry.isDirectory) {
          uploadDroppedFolder(entry, destDir);
          continue;
        }
        fileEntries.push(entry);
      }
      const readEntryFile = (entry) =>
        new Promise((resolve) => {
          try {
            entry.file(
              (f) => resolve(f),
              () => resolve(null)
            );
          } catch {
            resolve(null);
          }
        });
      (async () => {
        // The File handles first (cheap: no contents are read), so the whole
        // drop is one batch and every clash is asked before any bytes move.
        const files = [];
        for (const entry of fileEntries) {
          const f = await readEntryFile(entry);
          if (f) files.push(f);
        }
        await transferBatch(
          'upload',
          files.map((f) => ({ name: f.name, start: (meta) => uploadBrowserFile(f, destDir, meta) })),
          destDir
        );
      })();
    },
    [
      startPathUpload, startDownload, uploadBrowserFile, transferBatch, uploadFolder,
      downloadFolder, uploadDroppedFolder,
    ]
  );

  return { doArchive, openCompress, moveEntriesTo, makeRowDropHandler, makeDropHandler };
}
